import { v4 as uuidv4 } from 'uuid';
import { splitSegment, getAllSegments } from './compositeUtils';
import { updateCaptions } from './captionUtils';
import { getPlayerTimeFromSegmentTime } from '../timeUtils';
import { Clip } from '@/domains/asset';
import { TrackType } from '@/App/remotion/MainPlayer/types/Track/Type';
import { ElementsTrack } from '@/App/remotion/MainPlayer/types/Track/ElementsTrack';
import { Element } from '@/App/remotion/MainPlayer/types/Element/Element';
import { getClipFPS } from '@/libs/clipUtils';
import { getPlayerConfig } from '@/App/remotion/MainPlayer/utils/playerConfig/config';
import { injectSegmentMetaDataInCompositeSegment } from '@/App/remotion/MainPlayer/utils/playerConfig/configWithSplits';
import { insertElementIntoNewNonOverlappingTrack } from '@/Pages/Clip/ClipPlayer/ClipAssets/utils';

export const shiftElementTracksRight = (tracks: ElementsTrack[], offsetFrames: number): ElementsTrack[] => {
  return tracks.map(track => ({
    ...track,
    elements: track.elements.map(el => ({
      ...el,
      fromFrame: el.fromFrame + offsetFrames,
      toFrame: el.toFrame + offsetFrames
    }))
  }));
};

export const cloneOverlaysForHook = (
  clipData: Clip,
  selectionStartFrame: number,
  hookDurationFrames: number,
  baseTracks: ElementsTrack[] = []
): ElementsTrack[] => {
  const selectionEndFrame = selectionStartFrame + hookDurationFrames;
  const tracks = clipData.asset_metadata.player_config?.playerConfig?.tracks ?? [];
  const elementTracks = tracks.filter(track => track.type === TrackType.ELEMENTS);

  let clonedTracks: ElementsTrack[] = baseTracks;
  for (const track of elementTracks) {
    for (const element of track.elements) {
      if (element.toFrame <= selectionStartFrame || element.fromFrame >= selectionEndFrame) {
        continue;
      }

      const clonedFromFrame = Math.max(0, element.fromFrame - selectionStartFrame);
      const clonedToFrame = Math.min(hookDurationFrames, element.toFrame - selectionStartFrame);
      if (clonedFromFrame >= clonedToFrame) {
        continue;
      }

      const clonedElement: Element = {
        ...structuredClone(element),
        id: uuidv4(),
        fromFrame: clonedFromFrame,
        toFrame: clonedToFrame
      };
      clonedTracks = insertElementIntoNewNonOverlappingTrack(clonedTracks, clonedElement);
    }
  }
  return clonedTracks;
};

export const buildHookElementTracks = ({
  clip,
  playerStartTime,
  playerEndTime
}: {
  clip: Clip;
  playerStartTime: number;
  playerEndTime: number;
}): ElementsTrack[] => {
  const fps = getClipFPS(clip);
  const selectionStartFrame = Math.round(playerStartTime * fps);
  const selectionEndFrame = Math.round(playerEndTime * fps);
  const hookDurationFrames = Math.max(0, selectionEndFrame - selectionStartFrame);

  const originalElementTracks = (clip.asset_metadata.player_config?.playerConfig?.tracks ?? []).filter(
    t => t.type === TrackType.ELEMENTS
  );

  const shiftedElementTracks = shiftElementTracksRight(originalElementTracks, hookDurationFrames);
  return cloneOverlaysForHook(clip, selectionStartFrame, hookDurationFrames, shiftedElementTracks);
};

const normalizeTransientSplitClip = (clip: Clip): Clip => {
  updateCaptions(clip);
  clip.asset_metadata.player_config = getPlayerConfig({ clip, shouldUpdatePlayerConfig: true });

  return clip;
};

const getClipAfterTransientSplit = (baseClip: Clip, splitPlayerTime: number): Clip => {
  const splitClip = injectSegmentMetaDataInCompositeSegment(baseClip);
  splitSegment(splitClip, splitPlayerTime);
  return normalizeTransientSplitClip(splitClip);
};

export const isolateHookSelectionSegment = ({
  baseClip,
  startTime,
  endTime,
  segmentId
}: {
  baseClip: Clip;
  startTime: number;
  endTime: number;
  segmentId: string;
}) => {
  const [selectionStartTime, selectionEndTime] = startTime <= endTime ? [startTime, endTime] : [endTime, startTime];
  const playerStartTime = getPlayerTimeFromSegmentTime(selectionStartTime, baseClip, segmentId);
  const clipAfterStartSplit = getClipAfterTransientSplit(baseClip, playerStartTime);

  const segmentsAfterStartSplit = getAllSegments(clipAfterStartSplit);
  const originalSegmentIdx = segmentsAfterStartSplit.findIndex(s => s.id === segmentId);
  const segmentCreatedByStartSplit = segmentsAfterStartSplit[originalSegmentIdx + 1];
  if (!segmentCreatedByStartSplit) {
    throw new Error('CREATE_HOOK failed: start split did not isolate selection segment');
  }

  const playerEndTime = getPlayerTimeFromSegmentTime(
    selectionEndTime,
    clipAfterStartSplit,
    segmentCreatedByStartSplit.id
  );
  const clipAfterEndSplit = getClipAfterTransientSplit(clipAfterStartSplit, playerEndTime);
  const isolatedSelectionSegment = getAllSegments(clipAfterEndSplit).find(s => s.id === segmentCreatedByStartSplit.id);

  if (!isolatedSelectionSegment) {
    throw new Error('CREATE_HOOK failed: end split did not preserve isolated selection segment');
  }

  return {
    isolatedSelectionSegment,
    playerStartTime,
    playerEndTime
  };
};
